import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { getBrandsCatalog, loadCatalogManifest } from "@/lib/manifest";

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const letterOf = (name) => {
  const c = (name || "").trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : "#";
};

// Curated featured brands (matches TopBrands rail) shown before the alphabet grid.
const FEATURED_SLUGS = [
  "3m-espe",
  "dentsply",
  "nsk",
  "woodpecker",
  "ivoclar-vivadent",
  "gc",
  "mani",
  "coltene-whaledent",
  "kerr",
  "septodont",
  "prime-dental",
  "waldent",
];

export default function BrandsPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState(null);

  useEffect(() => {
    loadCatalogManifest().then(() => {
      setBrands(getBrandsCatalog());
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = brands;
    if (term) rows = rows.filter((b) => (b.name || "").toLowerCase().includes(term));
    if (letter) rows = rows.filter((b) => letterOf(b.name) === letter);
    return rows;
  }, [brands, q, letter]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const b of filtered) {
      const L = letterOf(b.name);
      if (!map.has(L)) map.set(L, []);
      map.get(L).push(b);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const availableLetters = useMemo(() => {
    const s = new Set();
    for (const b of brands) s.add(letterOf(b.name));
    return s;
  }, [brands]);

  const featured = useMemo(() => {
    if (!brands.length) return [];
    const bySlug = new Map(brands.map((b) => [b.slug, b]));
    return FEATURED_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
  }, [brands]);

  return (
    <main className="dyntra-container py-10">
      <p className="text-xs font-bold uppercase tracking-widest text-[#4B2AAD]">Brand Directory</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">All Brands</h1>
      <p className="text-sm text-gray-500 mt-1">
        Shop {brands.length}+ trusted dental brands. Browse alphabetically or search below.
      </p>

      <div className="mt-6 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          data-testid="brands-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search brands (3M, NSK, ...)"
          className="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 text-sm bg-white outline-none focus:border-[#4B2AAD]"
        />
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" /></div>
      ) : (
        <>
          {!q && !letter && featured.length > 0 && (
            <section className="mt-8">
              <h2 className="font-display text-lg font-bold text-gray-900 mb-3">Featured brands</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {featured.map((b) => (
                  <Link
                    key={b.slug}
                    to={`/brand/${b.slug}`}
                    data-testid={`brand-featured-${b.slug}`}
                    className="dyntra-card p-3 flex items-center justify-center h-16 group hover:border-[#4B2AAD]/40"
                  >
                    <span className="font-display text-sm font-bold text-gray-900 group-hover:text-[#4B2AAD] text-center leading-tight">{b.name}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <nav className="mt-8 flex flex-wrap gap-1.5" aria-label="Jump to letter">
            <button
              type="button"
              data-testid="brands-letter-all"
              onClick={() => setLetter(null)}
              className={`h-8 min-w-[32px] px-2 rounded-lg text-xs font-bold ${letter === null ? "bg-[#4B2AAD] text-white" : "border border-gray-200 text-gray-700 hover:bg-gray-50"}`}
            >
              All
            </button>
            {ALPHA.map((L) => {
              const enabled = availableLetters.has(L);
              const active = letter === L;
              return (
                <button
                  key={L}
                  type="button"
                  data-testid={`brands-letter-${L}`}
                  onClick={() => setLetter(active ? null : L)}
                  disabled={!enabled}
                  className={`h-8 min-w-[32px] px-2 rounded-lg text-xs font-bold ${active ? "bg-[#4B2AAD] text-white" : enabled ? "border border-gray-200 text-gray-700 hover:bg-gray-50" : "border border-gray-100 text-gray-300 cursor-not-allowed"}`}
                >
                  {L}
                </button>
              );
            })}
          </nav>

          {grouped.length === 0 ? (
            <p className="mt-10 text-sm text-gray-500">No brands match your search.</p>
          ) : (
            <div className="mt-8 space-y-8">
              {grouped.map(([L, list]) => (
                <section key={L}>
                  <h2 className="font-display text-lg font-bold text-gray-900 mb-3 sticky top-0 bg-white py-1 z-10">{L}</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {list.map((b) => (
                      <Link
                        key={b.slug}
                        to={`/brand/${b.slug}`}
                        data-testid={`brand-tile-${b.slug}`}
                        className="dyntra-card px-4 py-3 flex items-center justify-between group hover:border-[#4B2AAD]/40"
                      >
                        <div className="min-w-0">
                          <span className="block font-semibold text-gray-900 group-hover:text-[#4B2AAD] truncate">{b.name}</span>
                          {b.productCount > 0 && (
                            <span className="block text-[11px] text-gray-500">{b.productCount} SKUs</span>
                          )}
                        </div>
                        <span aria-hidden className="text-[#4B2AAD] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          <p className="mt-10 text-xs text-gray-400">
            Showing {filtered.length} of {brands.length} brands.
          </p>
        </>
      )}
    </main>
  );
}
